<template>
  <div class="mfa-account">
    <template v-if="status">
      <!-- Statut + action principale (activer / désactiver) -->
      <div class="item mfa-status">
        <div class="name">
          <h5>
            {{ $t('mfa.settings.title') }}
            <span v-if="status.enabled" class="mfa-badge"><i class="fa-solid fa-check"></i>{{ $t('mfa.status.enabled_short') }}</span>
          </h5>
          <p v-if="status.required && !status.enabled" class="mfa-warning"><i class="fa-regular fa-triangle-exclamation"></i>{{ $t('mfa.settings.required_next_login') }}</p>
          <p v-else-if="status.required">{{ $t('mfa.settings.required_by_admin') }}</p>
          <p v-else>{{ $t('mfa.settings.subtitle') }}</p>
          <p v-if="status.enabled">{{ $t('mfa.settings.recovery_left', { count: status.recovery_left }) }}</p>
          <p v-if="status.impersonated" class="mfa-warning">{{ $t('mfa.settings.impersonated') }}</p>
        </div>
        <div class="form">
          <template v-if="!status.impersonated && !setupData && !pendingAction && !recoveryCodes">
            <div v-if="!status.enabled" class="btn-secondary btn" style="margin-left: auto; width: 50%;" @click="startSetup">{{ $t('mfa.settings.enable') }}</div>
            <!-- Active : codes de secours + désactivation (sauf si exigée) sur la même ligne -->
            <div v-else class="mfa-row mfa-actions">
              <div class="btn-outline-primary btn" :style="status.required ? 'margin-left: auto; max-width: 50%;' : ''" @click="askCode('regenerate')">{{ $t('mfa.settings.regenerate') }}</div>
              <div v-if="!status.required" class="btn-secondary btn" @click="askCode('disable')">{{ $t('mfa.settings.disable_title') }}</div>
            </div>
          </template>
        </div>
      </div>

      <!-- Activation : QR code puis code de vérification -->
      <div v-if="setupData" class="item mfa-setup">
        <div class="name">
          <h5>{{ $t('mfa.setup.title') }}</h5>
          <p>{{ $t('mfa.setup.scan') }}</p>
          <p>{{ $t('mfa.setup.manual_key') }}</p>
          <p><code class="mfa-secret">{{ setupData.secret }}</code></p>
        </div>
        <div class="form mfa-setup-form">
          <img :src="setupData.qr" alt="QR code" class="mfa-qr" />
          <div class="mfa-setup-fields">
            <div class="input">
              <i class="fa-regular fa-shield-check"></i>
              <input v-model="code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="123456" @keyup.enter="activate">
            </div>
            <p v-if="error" class="mfa-error">{{ error }}</p>
            <div class="mfa-row">
              <div class="btn-outline-primary btn" @click="cancelSetup">{{ $t('dash.button.cancel') }}</div>
              <div class="btn-secondary btn" @click="activate">{{ $t('dash.button.validate') }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Confirmation par code (désactivation, régénération des codes) -->
      <div v-if="pendingAction" class="item mfa-confirm">
        <div class="name">
          <h5>{{ $t('mfa.settings.confirm_title') }}</h5>
          <p>{{ useRecovery ? $t('mfa.login.recovery_subtitle') : $t('mfa.login.totp_subtitle') }}</p>
          <p><a class="underline mfa-link" @click.prevent="toggleRecovery">{{ useRecovery ? $t('mfa.login.use_app') : $t('mfa.login.use_recovery') }}</a></p>
          <p v-if="error" class="error">{{ error }}</p>
        </div>
        <div class="form mfa-row">
          <div class="input">
            <i class="fa-regular fa-shield-check"></i>
            <input v-model="code" :inputmode="useRecovery ? 'text' : 'numeric'" autocomplete="one-time-code" :maxlength="useRecovery ? 9 : 6" :placeholder="useRecovery ? 'XXXX-XXXX' : '123456'" @keyup.enter="confirmAction">
          </div>
          <div class="btn-outline-primary btn" @click="pendingAction = null">{{ $t('dash.button.cancel') }}</div>
          <div class="btn-secondary btn" @click="confirmAction">{{ $t('dash.button.validate') }}</div>
        </div>
      </div>

      <!-- Codes de secours : affichés une seule fois après activation / régénération -->
      <div v-if="recoveryCodes" class="item mfa-recovery">
        <div class="name">
          <h5>{{ $t('mfa.recovery.title') }}</h5>
        </div>
        <div class="form">
          <RecoveryCodes :codes="recoveryCodes" @done="recoveryCodes = null" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import SettingsMixins from '../../mixins/Settings.vue'
import RecoveryCodes from './RecoveryCodes.vue'

// Onglet Mon compte : activation / désactivation de la double authentification du user connecté
export default {
  name: 'account-mfa',
  components: { RecoveryCodes },
  mixins: [SettingsMixins],
  data() {
    return {
      status: null,
      setupData: null,
      recoveryCodes: null,
      pendingAction: null,
      useRecovery: false,
      code: '',
      error: null
    }
  },
  mounted() {
    this.refresh()
  },
  methods: {
    errorText(res) {
      const key = res && res.body && res.body.error
      return key && this.$te(key) ? this.$t(key) : this.$t('mfa.error.generic')
    },
    async refresh() {
      const res = await this.mfaStatus()
      if (res.ok) this.status = res.body
    },
    async startSetup() {
      this.error = null
      this.code = ''
      const res = await this.mfaSetup()
      if (!res.ok) return this.$vs.notify({ title: this.$t('mfa.error.title'), text: this.errorText(res), color: 'danger' })
      this.setupData = res.body
    },
    cancelSetup() {
      this.setupData = null
      this.code = ''
      this.error = null
    },
    async activate() {
      if (!this.code) return
      const res = await this.mfaActivate(this.code)
      if (!res.ok) {
        this.error = this.errorText(res)
        this.code = ''
        return
      }
      this.setupData = null
      this.code = ''
      this.recoveryCodes = res.body.recovery_codes
      await this.refresh()
      this.$vs.notify({ title: this.$t('mfa.settings.title'), text: this.$t('mfa.settings.enabled_notify'), color: 'success' })
    },
    askCode(action) {
      this.pendingAction = action
      this.useRecovery = false
      this.code = ''
      this.error = null
    },
    toggleRecovery() {
      this.useRecovery = !this.useRecovery
      this.code = ''
    },
    async confirmAction() {
      if (!this.code) return
      const body = { code: this.code, method: this.useRecovery ? 'recovery' : 'totp' }
      const res = this.pendingAction === 'disable' ? await this.mfaDisable(body) : await this.mfaRegenerateCodes(body)
      if (!res.ok) {
        this.error = this.errorText(res)
        this.code = ''
        return
      }
      if (this.pendingAction === 'regenerate') this.recoveryCodes = res.body.recovery_codes
      else this.$vs.notify({ title: this.$t('mfa.settings.title'), text: this.$t('mfa.settings.disabled_notify'), color: 'success' })
      this.pendingAction = null
      this.code = ''
      await this.refresh()
    }
  }
}
</script>

<style lang="scss" scoped>
.mfa-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 20px;
  background: #e3f6e8;
  color: #1e7b34;
  font-size: 11px;
  font-weight: 600;
  vertical-align: middle;
}
// Plus spécifique que le style global de account.scss (.account-settings .section-account .item .name p)
.mfa-account .item .name p.mfa-warning {
  color: #b45309;
}
.mfa-secret {
  font-size: 13px;
  letter-spacing: 1px;
  color: #121826;
  user-select: all;
}
.mfa-setup-form {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.mfa-qr {
  width: 140px;
  height: 140px;
  flex-shrink: 0;
}
.mfa-setup-fields {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.mfa-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  & > div {
    width: 0;
    flex-grow: 1;
    min-width: 120px;
  }
  & > .input {
    flex-grow: 2;
  }
}
// Deux boutons côte à côte : prime sur `.item .btn { width: 100% }` de account.scss
.mfa-account .item .mfa-actions {
  flex-wrap: nowrap;
  & > .btn {
    width: 0;
    flex: 1 1 0;
    min-width: 0;
    white-space: nowrap;
  }
}
.mfa-error {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #FF5C5C;
}
.mfa-link {
  cursor: pointer;
}
</style>
